import { Link } from "react-router-dom";
import { Badge } from "../../../shared/components/ui/badge";
import type { Provider } from "../hooks/useProviders";

interface ProviderCardProps {
  readonly provider: Provider;
}

/** Badge variants for submission status */
type StatusVariant = "draft" | "pending" | "approved" | "rejected" | "published";

/** Default locale for date formatting */
const DEFAULT_LOCALE = "en-US";

/** Map submission status to badge variant */
const STATUS_VARIANT_MAP: Record<string, StatusVariant> = {
  DRAFT: "draft",
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  PUBLISHED: "published",
} as const;

/**
 * Provider Card Component
 * Displays content provider information with submission statistics
 */
export function ProviderCard({ provider }: ProviderCardProps) {
  const { stats } = provider;

  // Format date
  const joinedDate = new Date(provider.createdAt).toLocaleDateString(DEFAULT_LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  // Get status badge variant
  const getStatusVariant = (status: string): StatusVariant => {
    return STATUS_VARIANT_MAP[status.toUpperCase()] || "draft";
  };

  return (
    <div className="border border-neutral-800 rounded-lg p-6 hover:border-neutral-700 transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white">{provider.email}</h3>
          {provider.organization && <p className="text-sm text-neutral-400 mt-1">{provider.organization}</p>}
          <p className="text-xs text-neutral-500 mt-1">Joined {joinedDate}</p>
        </div>

        {/* Total submissions badge */}
        <div className="text-center">
          <div className="text-2xl font-bold text-white">{stats.total}</div>
          <div className="text-xs text-neutral-400">Submissions</div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        <StatItem label="Draft" count={stats.draft} variant="neutral" />
        <StatItem label="Pending" count={stats.pending} variant="warning" />
        <StatItem label="Approved" count={stats.approved} variant="success" />
        <StatItem label="Rejected" count={stats.rejected} variant="error" />
        <StatItem label="Published" count={stats.published} variant="info" />
      </div>

      {/* Recent Submission */}
      {provider.recentSubmission && (
        <div className="border-t border-neutral-800 pt-4 mb-4">
          <p className="text-xs text-neutral-500 mb-2">Most Recent Submission</p>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{provider.recentSubmission.title}</p>
              <p className="text-xs text-neutral-500">
                {new Date(provider.recentSubmission.createdAt).toLocaleDateString()}
              </p>
            </div>
            <Badge variant={getStatusVariant(provider.recentSubmission.status)} className="ml-2 flex-shrink-0">
              {provider.recentSubmission.status}
            </Badge>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Link to={`/admin/providers/${provider.id}`} className="btn-admin-action">
          View Details
        </Link>
        <Link to={`/admin/review-queue?provider=${provider.id}`} className="btn-admin-action">
          View Submissions
        </Link>
      </div>
    </div>
  );
}

/**
 * Stat Item Component
 * Small stat display for submission counts by status
 */
interface StatItemProps {
  readonly label: string;
  readonly count: number;
  readonly variant: "neutral" | "warning" | "success" | "error" | "info";
}

/** Map stat variant to Tailwind color classes */
const STAT_VARIANT_COLORS: Record<StatItemProps["variant"], string> = {
  neutral: "text-neutral-400",
  warning: "text-yellow-500",
  success: "text-green-500",
  error: "text-red-500",
  info: "text-blue-500",
} as const;

function StatItem({ label, count, variant }: StatItemProps) {
  return (
    <div className="text-center">
      <div className={`text-lg font-bold ${STAT_VARIANT_COLORS[variant]}`}>{count}</div>
      <div className="text-xs text-neutral-500">{label}</div>
    </div>
  );
}
